import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  TokenGroup,
} from "@amzn/awsui-components-react/polaris";

const TYPE_OPTIONS = [
  { label: "Text", value: "TEXT" },
  { label: "Dropdown", value: "DROPDOWN" },
  { label: "Number", value: "NUMBER" },
  { label: "Date", value: "DATE" },
];

/**
 * "Add Column" dialog (admin only).
 *
 * Type-dependent fields:
 *   - Dropdown -> comma-separated values textarea
 *   - Date     -> date-type descriptor + "required when triggered" / "include in
 *                 daily digest" checkboxes
 * "Add Column" stays disabled until the required fields for the chosen type are
 * filled.
 */
const AddColumnModal = ({
  visible,
  onDismiss,
  onAdd,
  initialColumn = null,
  error = "",
  saving = false,
}) => {
  const [type, setType] = useState(null);
  const [columnName, setColumnName] = useState("");
  const [dropdownValues, setDropdownValues] = useState([]); // committed tokens
  const [dropdownInput, setDropdownInput] = useState(""); // in-progress text
  const [dateType, setDateType] = useState("");
  const [requiredWhenTriggered, setRequiredWhenTriggered] = useState(false);
  const [includeInDigest, setIncludeInDigest] = useState(false);

  const typeValue = type?.value || null;

  const reset = () => {
    setType(null);
    setColumnName("");
    setDropdownValues([]);
    setDropdownInput("");
    setDateType("");
    setRequiredWhenTriggered(false);
    setIncludeInDigest(false);
  };

  const isEditing = !!initialColumn;

  useEffect(() => {
    if (!visible) return;
    if (!initialColumn) {
      reset();
      return;
    }

    setType(TYPE_OPTIONS.find((option) => option.value === initialColumn.type) || null);
    setColumnName(initialColumn.name || "");
    setDropdownValues(Array.isArray(initialColumn.values) ? initialColumn.values : []);
    setDropdownInput("");
    setDateType(initialColumn.due_date_type || initialColumn.dateType || "");
    setRequiredWhenTriggered(
      !!(initialColumn.required_when_triggered ?? initialColumn.requiredWhenTriggered)
    );
    setIncludeInDigest(!!(initialColumn.include_in_digest ?? initialColumn.includeInDigest));
  }, [visible, initialColumn]);

  // Commit the typed text as a token on Enter; ignore blanks/duplicates.
  const commitDropdownValue = () => {
    const val = dropdownInput.trim();
    if (val && !dropdownValues.includes(val)) {
      setDropdownValues((prev) => [...prev, val]);
    }
    setDropdownInput("");
  };

  const handleDismiss = () => {
    reset();
    onDismiss?.();
  };

  const isValid =
    !!typeValue &&
    columnName.trim().length > 0 &&
    (typeValue !== "DROPDOWN" || dropdownValues.length > 0);

  const handleAdd = async () => {
    if (!isValid || saving) return;
    const column = { type: typeValue, name: columnName.trim() };
    if (typeValue === "DROPDOWN") column.values = dropdownValues;
    if (typeValue === "DATE") {
      column.dateType = dateType.trim();
      column.requiredWhenTriggered = requiredWhenTriggered;
      column.includeInDigest = includeInDigest;
    }
    const added = await onAdd?.(column);
    if (added !== false) reset();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={isEditing ? "Edit Column" : "Add Column"}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!isValid || saving}
              loading={saving}
              onClick={handleAdd}
            >
              {isEditing ? "Save Changes" : "Add Column"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header={isEditing ? "Couldn't update column" : "Couldn't add column"}>
            {error}
          </Alert>
        )}
        <FormField label={<>Type <span style={{ color: "#d13212" }}>*</span></>}>
          <Select
            placeholder="Choose type"
            selectedOption={type}
            options={TYPE_OPTIONS}
            onChange={({ detail }) => setType(detail.selectedOption)}
          />
        </FormField>

        <FormField label={<>Column Name <span style={{ color: "#d13212" }}>*</span></>}>
          <Input
            placeholder="Enter column name"
            value={columnName}
            onChange={({ detail }) => setColumnName(detail.value)}
          />
        </FormField>

        {typeValue === "DROPDOWN" && (
          <FormField
            label="Dropdown Values"
            description="Type a value and press Enter to add it"
          >
            <SpaceBetween size="xs">
              <Input
                placeholder="Type a value and press Enter"
                value={dropdownInput}
                onChange={({ detail }) => setDropdownInput(detail.value)}
                onKeyDown={({ detail }) => {
                  if (detail.key === "Enter") {
                    commitDropdownValue();
                  }
                }}
              />
              <TokenGroup
                items={dropdownValues.map((v) => ({ label: v, dismissLabel: `Remove ${v}` }))}
                onDismiss={({ detail }) =>
                  setDropdownValues((prev) => prev.filter((_, i) => i !== detail.itemIndex))
                }
              />
            </SpaceBetween>
          </FormField>
        )}

        {typeValue === "DATE" && (
          <>
            <FormField
              label="Date Type"
              description="Describe what this date represents and what to show on the services screen during upload/trigger (e.g. Pre WB, Post WB)"
            >
              <Input
                placeholder="e.g. Pre WB"
                value={dateType}
                onChange={({ detail }) => setDateType(detail.value)}
              />
            </FormField>
            <SpaceBetween size="xs">
              <Checkbox
                checked={requiredWhenTriggered}
                onChange={({ detail }) => setRequiredWhenTriggered(detail.checked)}
              >
                Required when service is triggered
              </Checkbox>
              <Checkbox
                checked={includeInDigest}
                onChange={({ detail }) => setIncludeInDigest(detail.checked)}
              >
                Include in daily digest
              </Checkbox>
            </SpaceBetween>
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default AddColumnModal;
